import { SizeType } from 'antd/es/config-provider/SizeContext';
import React, { FC, RefObject, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { TaskId } from 'api/models';
import { StatusUpdateState } from 'api/status-updates';
import { ReactComponent as NoContentSvg } from 'assets/svgs/empty-states/no-content-1.svg';
import { Button } from 'components/atoms/button';
import { Empty } from 'components/atoms/empty';
import { InputType } from 'components/atoms/input';
import { NewRow } from 'components/molecules/new-row';
import { HideIfNotAllowed } from 'components/organisms/permission-controller';
import { HelperText01, Text03 } from 'components/styles';
import { PermissionRuleName } from 'constant';
import { selectActiveStatusUpdate } from 'data/status-updates';
import { Task } from 'data/tasks';
import { deleteTaskSagaAction, updateTaskSagaAction } from 'data/tasks/actions';
import { queueMacrotask } from 'utils/macrotask';
import { Table } from './table';
import { TaskPicker } from './tasks-picker';
import styles from './tasks-table.module.css';

interface Props {
  size?: SizeType;
  projectId: number;
  data: Array<Task>;
  refTo?: RefObject<HTMLDivElement>;
  onTaskNameClick: (task: TaskId) => void;
  onDueDateChange?: (
    taskId: number,
    payload: {
      startDate?: number;
      dueDate?: number;
    },
  ) => void;
  onAssigneeChange?: (taskId: number, payload) => void;
  onDelete?: (task: Task) => void;
  handleNewTaskAdded: (taskName: string) => Promise<void>;
  handleMultipleTasksAdded?: (tasks: Array<string>) => Promise<void>;
  documentSectionId?: number;
  resourceSectionId?: number;
  newRowPlaceholder?: string;
  hideEmptyStateImage?: boolean;
  isPrivate?: boolean;
  onSort?: (sorted: Task[]) => void;
  isSubTask?: boolean;
  isReadOnly?: boolean;
  autoFocus?: boolean;
  useClearAction?: boolean;
}
export const EmptyMessage = styled.span`
  ${HelperText01};
  ${Text03};
`;

export const TaskTable: FC<Props> = (props) => {
  const {
    size = 'middle',
    projectId,
    data = [],
    refTo,
    handleNewTaskAdded,
    handleMultipleTasksAdded,
    newRowPlaceholder,
    onDelete,
    onDueDateChange,
    onAssigneeChange,
    hideEmptyStateImage = false,
    isPrivate,
    isSubTask,
    isReadOnly,
    resourceSectionId,
    documentSectionId,
    autoFocus = true,
    useClearAction,
    ...rest
  } = props;

  const [canShowEmpty, setCanShowEmpty] = useState<boolean>(!data.length);
  const [canShowNewRow, setCanShowNewRow] = useState(false);

  const dispatch = useDispatch();
  const activeStatusUpdate = useSelector(selectActiveStatusUpdate);
  const pathName = window.location.pathname;
  const isPublished =
    pathName.includes('status-updates') &&
    activeStatusUpdate?.state === StatusUpdateState.Published;
  const hideByUrl =
    pathName.includes('spaces') || pathName.includes('status-updates');

  useEffect(() => {
    setCanShowEmpty(!data.length);
  }, [data.length]);

  const handleAssigneeChange = (
    taskId: number,
    assignee,
    resolveCallback?: (value) => void,
  ) => {
    if (onAssigneeChange) {
      onAssigneeChange(taskId, { assignee });
    } else {
      dispatch(
        updateTaskSagaAction({
          projectId,
          taskId,
          data: {
            assignee,
          },
          options: {
            resolveCallback,
          },
        }),
      );
    }
  };

  const handleDueDateChange = (taskId, payload) => {
    if (onDueDateChange) {
      onDueDateChange(taskId, payload);
    } else {
      dispatch(updateTaskSagaAction({ projectId, taskId, data: payload }));
    }
  };

  const handleDelete = (task: Task) => {
    if (onDelete) {
      onDelete(task);
    } else {
      dispatch(deleteTaskSagaAction({ projectId, taskId: task.taskId }));
    }
  };
  const inputRef = useRef<InputType>(null);
  const handleAddClick = () => {
    if (hideByUrl) {
      setCanShowNewRow(true);
    }
    setCanShowEmpty(false);
    queueMacrotask(() => inputRef.current?.focus());
  };

  const onClear = () => {
    setCanShowNewRow(false);
    setCanShowEmpty(!data.length);
  };

  const addTasksSection = () => (
    <div className={styles.add_task}>
      <Button
        ref={refTo}
        onClick={isReadOnly ? undefined : handleAddClick}
        className={styles.add_btn}
        type="link"
        size="small"
        ruleName={PermissionRuleName.TaskCreateAction}
        data-cy="add_task.button"
      >
        <FormattedMessage id="task.add" />
      </Button>

      {resourceSectionId && documentSectionId ? (
        <div>
          <HideIfNotAllowed ruleName={PermissionRuleName.TaskCreateAction}>
            <FormattedMessage id="task.new.or.existing" />
          </HideIfNotAllowed>
          <TaskPicker
            projectId={projectId}
            isPrivate={isPrivate}
            resourceSectionId={resourceSectionId}
            documentSectionId={documentSectionId}
            isReadOnly={isReadOnly}
          />
        </div>
      ) : null}
    </div>
  );

  return (
    <div className={isPrivate ? styles.private : ''}>
      {!canShowEmpty ? (
        <div ref={refTo}>
          {data.length > 0 && (
            <Table
              handleDueDateChange={handleDueDateChange}
              handleAssigneeChange={handleAssigneeChange}
              handleDelete={handleDelete}
              data={data}
              size={size}
              isSubTask={isSubTask}
              isSuPublished={isPublished}
              isReadOnly={isReadOnly}
              {...rest}
            />
          )}
          {(!hideByUrl || (!isPublished && canShowNewRow)) && !isReadOnly ? (
            <HideIfNotAllowed ruleName={PermissionRuleName.TaskCreateAction}>
              <NewRow
                autoFocus={autoFocus}
                inputRef={inputRef}
                size={size}
                onAdd={handleNewTaskAdded}
                onAddMultiple={handleMultipleTasksAdded}
                placeholder={newRowPlaceholder}
                onClear={onClear}
                useClearAction={useClearAction}
                bulkAddType="subtasks"
                showStatusIcon
              />
            </HideIfNotAllowed>
          ) : null}
          {hideByUrl && !isPublished && !canShowNewRow
            ? addTasksSection()
            : null}
        </div>
      ) : (
        <Empty hideImage={hideEmptyStateImage} isPrivate={isPrivate}>
          {!hideByUrl || !isPublished ? addTasksSection() : <></>}
          <NoContentSvg />
        </Empty>
      )}
    </div>
  );
};
